// src/components/ClaimAgendaPage.jsx
// "Mi agenda" — pantalla nueva en el portal /claim-status/agenda con TODAS
// las citas agendadas del ajustador (o de toda la empresa si es
// supervisor con viewAllClaims), a través de todos sus reclamos, no una
// por una como ClaimSchedule.jsx (que vive adentro de un solo reclamo).
// Mismo gate de auth que el resto del portal.
import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { LanguageContext } from '../App';
import { EVENT_TYPE_LABELS } from '../services/claimEventService';
import { getChecksByClaimRecordId } from '../services/claimService';
import { useAdjusterEvents, isPastEvent as isPast } from '../hooks/useAdjusterEvents';
import Login from './Login';
import WorkspacePreferences from './WorkspacePreferences';
import InitialPasswordChange from './InitialPasswordChange';
import './Dashboard/Dashboard.css';
import './Dashboard/ClaimsDirectory.css';
import './ClaimStatus.css';

const formatEventDate = (date, language) => {
  if (!date) return '—';
  const parsed = new Date(`${date}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return new Intl.DateTimeFormat(language === 'es' ? 'es-ES' : 'en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
  }).format(parsed);
};

const toDateKey = (year, month, day) => `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

// Grilla del mes siempre expandida (sin toggle) — pedido explícito
// 2026-09-19. Domingo primero, célda por día con las citas de ese día
// como chips clickeables (abren el reclamo, igual que en la lista de abajo).
const MonthCalendar = ({ events, monthDate, onPrevMonth, onNextMonth, onOpenEvent, openingClaim, language }) => {
  const es = language === 'es';
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayKey = toDateKey(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());

  const eventsByDay = {};
  events.forEach(e => {
    if (!e.date) return;
    (eventsByDay[e.date] = eventsByDay[e.date] || []).push(e);
  });

  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) cells.push(day);

  const weekdayLabels = es
    ? ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const monthLabel = new Intl.DateTimeFormat(es ? 'es-ES' : 'en-US', { month: 'long', year: 'numeric' }).format(monthDate);

  return (
    <div className="cd-cal">
      <div className="cd-cal-header">
        <button type="button" className="cd-attach-mini-btn" onClick={onPrevMonth} title={es ? 'Mes anterior' : 'Previous month'}>
          <i className="fa-solid fa-chevron-left"></i>
        </button>
        <h3 className="cd-cal-month-label">{monthLabel}</h3>
        <button type="button" className="cd-attach-mini-btn" onClick={onNextMonth} title={es ? 'Mes siguiente' : 'Next month'}>
          <i className="fa-solid fa-chevron-right"></i>
        </button>
      </div>
      <div className="cd-cal-grid cd-cal-weekdays">
        {weekdayLabels.map(w => <div key={w} className="cd-cal-weekday">{w}</div>)}
      </div>
      <div className="cd-cal-grid">
        {cells.map((day, i) => {
          if (day === null) return <div key={`empty-${i}`} className="cd-cal-cell cd-cal-cell-empty"></div>;
          const dateKey = toDateKey(year, month, day);
          const dayEvents = eventsByDay[dateKey] || [];
          return (
            <div key={dateKey} className={`cd-cal-cell ${dateKey === todayKey ? 'cd-cal-cell-today' : ''}`}>
              <span className="cd-cal-day-number">{day}</span>
              <div className="cd-cal-day-events">
                {dayEvents.map(event => {
                  const label = event.type ? (EVENT_TYPE_LABELS[event.type]?.[es ? 'es' : 'en'] || event.type) : event.title;
                  return (
                    <button
                      key={event.id}
                      type="button"
                      className={`cd-cal-event-chip ${isPast(event) ? 'cd-cal-event-chip-past' : ''} ${event.confirmed ? 'cd-cal-event-chip-confirmed' : ''} ${event.source === 'zoho' ? 'cd-cal-event-chip-zoho' : ''}`}
                      onClick={() => onOpenEvent(event)}
                      disabled={openingClaim === event.claimRecordId}
                      title={`${label}${event.clientName ? ` — ${event.clientName}` : ''}`}
                    >
                      {event.time && <span className="cd-cal-event-chip-time">{event.time}</span>}
                      <span className="cd-cal-event-chip-label" title={typeof label === 'string' ? label : undefined}>{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// embedded: dentro del menú del panel admin (sección Agenda) — sin el
// encabezado del portal ni el enlace de volver, y sin el padding de página.
const ClaimAgendaPage = ({ embedded = false }) => {
  const { language } = useContext(LanguageContext);
  const { isAuthenticated, isAdmin, mustChangePassword } = useAuth();
  const [openingClaim, setOpeningClaim] = useState(null);
  const [monthDate, setMonthDate] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const es = language === 'es';
  const monthKey = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, '0')}`;

  const { events, loading, zohoError } = useAdjusterEvents(monthKey);

  useEffect(() => {
    if (zohoError) toast.error(es ? 'No se pudieron cargar las reuniones de Zoho.' : 'Could not load Zoho meetings.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zohoError]);

  if (!isAuthenticated) return <Login />;
  if (!isAdmin && mustChangePassword) return <InitialPasswordChange />;

  // Al hacer clic en una cita, arma el link al detalle del reclamo — la
  // ruta necesita ?checks=id1,id2 (ver ClaimDetailPage.jsx), así que se
  // resuelve recién al abrir, no de entrada para toda la lista.
  const handleOpenClaim = async (claimRecordId) => {
    setOpeningClaim(claimRecordId);
    try {
      const checks = await getChecksByClaimRecordId(claimRecordId);
      // Sin cheques (reclamo nuevo o migrado) se abre igual la ficha por su id.
      window.open(checks.length > 0
        ? `/claim-status/claim/${claimRecordId}?checks=${checks.map(c => c.id).join(',')}`
        : `/claim-status/claim/${claimRecordId}`, '_blank');
    } catch (err) {
      toast.error(es ? 'No se pudo abrir el reclamo.' : 'Could not open the claim.');
    } finally {
      setOpeningClaim(null);
    }
  };

  // Una cita de la app abre el reclamo; una reunión que solo existe en
  // Zoho (sin claimRecordId) abre directo su link de Zoho en vez de eso.
  const handleOpenEvent = (event) => {
    if (event.claimRecordId) { handleOpenClaim(event.claimRecordId); return; }
    if (event.zohoUrl) window.open(event.zohoUrl, '_blank');
  };

  const handlePrevMonth = () => setMonthDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const handleNextMonth = () => setMonthDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));

  const upcoming = events.filter(e => !isPast(e));
  const history = events.filter(isPast);

  const renderEvent = (event) => {
    const label = event.type ? (EVENT_TYPE_LABELS[event.type]?.[es ? 'es' : 'en'] || event.type) : event.title;
    return (
      <div key={event.id} className={`cd-event-card ${isPast(event) ? 'cd-event-past' : ''}`}>
        <div className="cd-event-card-main">
          <span className="cd-event-datetime cd-event-date-primary">
            <i className="fa-regular fa-calendar"></i> {formatEventDate(event.date, language)}
            {event.time && <span> · {event.time}</span>}
          </span>
          <span className="cd-event-type cd-event-type-secondary">{label}</span>
          {event.confirmed !== null && (
            <span className={`cd-event-confirm-badge ${event.confirmed ? 'cd-event-confirm-yes' : 'cd-event-confirm-no'}`}>
              <i className={`fa-solid ${event.confirmed ? 'fa-check' : 'fa-clock'}`}></i>
              {event.confirmed ? (es ? 'Confirmada' : 'Confirmed') : (es ? 'Pendiente' : 'Pending')}
            </span>
          )}
          {event.source === 'zoho' && (
            <span className="cd-event-type-secondary"><i className="fa-solid fa-calendar-days"></i> Zoho</span>
          )}
        </div>
        {event.claimRecordId ? (
          <button
            type="button"
            className="cd-agenda-claim-link"
            onClick={() => handleOpenClaim(event.claimRecordId)}
            disabled={openingClaim === event.claimRecordId}
          >
            <i className="fa-solid fa-folder-open"></i>
            {event.clientName || (es ? 'Reclamo' : 'Claim')} {event.claimNumber ? `#${event.claimNumber}` : ''}
            <i className="fa-solid fa-arrow-up-right-from-square cd-agenda-claim-link-icon"></i>
          </button>
        ) : event.zohoUrl && (
          <a href={event.zohoUrl} target="_blank" rel="noopener noreferrer" className="cd-agenda-claim-link">
            <i className="fa-solid fa-calendar-days"></i> {es ? 'Ver en Zoho' : 'View in Zoho'}
            <i className="fa-solid fa-arrow-up-right-from-square cd-agenda-claim-link-icon"></i>
          </a>
        )}
        {event.location && <p className="cd-note-text"><i className="fa-solid fa-location-dot"></i> {event.location}</p>}
        {event.notes && <p className="cd-note-text">{event.notes}</p>}
      </div>
    );
  };

  return (
    <div className={embedded ? 'claim-status-page' : 'dashboard-modern claim-status-page'}>
      <div className="cd-container">
        {!embedded && (
          <div className="workspace-detail-toolbar">
            <Link to="/dashboard" className="cd-detail-back">
              <i className="fa-solid fa-arrow-left" aria-hidden="true"></i>
              {es ? 'Volver a reclamos' : 'Back to claims'}
            </Link>
            <WorkspacePreferences />
          </div>
        )}

        <div className="cd-detail-title-row">
          <div>
            {!embedded && <span className="cd-detail-eyebrow">{es ? 'Portal del ajustador' : 'Adjuster portal'}</span>}
            {/* Dentro del panel el título ya lo pone el Dashboard. */}
            {!embedded && <h1>{es ? 'Mi agenda' : 'My schedule'}</h1>}
            <p className="cd-detail-subtitle">
              {es ? 'Todas tus citas agendadas, a través de todos tus reclamos.' : 'All your scheduled appointments, across all your claims.'}
            </p>
          </div>
          <span className="cd-status-badge cd-status-count cd-detail-count-badge">
            <i className="fa-regular fa-calendar"></i>
            {events.length} {es ? (events.length === 1 ? 'cita' : 'citas') : (events.length === 1 ? 'appointment' : 'appointments')}
          </span>
        </div>

        {!loading && (
          <MonthCalendar
            events={events}
            monthDate={monthDate}
            onPrevMonth={handlePrevMonth}
            onNextMonth={handleNextMonth}
            onOpenEvent={handleOpenEvent}
            openingClaim={openingClaim}
            language={language}
          />
        )}

        <div className="cd-attach-explorer">
          <div className="cd-attach-body">
            {loading && <p className="cd-attach-empty">{es ? 'Cargando agenda...' : 'Loading schedule...'}</p>}
            {!loading && events.length === 0 && (
              <p className="cd-attach-empty">{es ? 'Todavía no tenés citas agendadas.' : 'You have no scheduled appointments yet.'}</p>
            )}
            {!loading && upcoming.length > 0 && (
              <>
                <h3 className="cd-schedule-group-title">{es ? 'Próximas citas' : 'Upcoming'}</h3>
                <div className="cd-notes-list">{upcoming.map(renderEvent)}</div>
              </>
            )}
            {!loading && history.length > 0 && (
              <>
                <h3 className="cd-schedule-group-title">{es ? 'Historial' : 'History'}</h3>
                <div className="cd-notes-list">{history.map(renderEvent)}</div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimAgendaPage;
